import {
  localStorageKeys,
  APP_VERSION_KEY,
  FORCE_RESET_KEY,
  VERSION_RESET_SESSION_KEY,
  PRESERVE_LS,
  CONTROL_LS,
  PRESERVE_COOKIES,
  PRESERVE_FILTER_COOKIES,
} from 'constants/localStorage';
import { URLS } from 'constants/urls';
import { getCookie } from 'utils/cookies';
import { saveTokensToCookies } from 'services/main/config';
import * as Sentry from '@sentry/react';

// IndexedDB que NUNCA pode ser apagado no reset: a fila de upload offline
// (upload-db / store `outbox`) guarda uploads pendentes — apagá-la = perda de dado.
const PROTECTED_IDB = ['upload-db'];

// Lê o set de versões já resetadas nesta sessão (tolerante a lixo/legado no valor).
const readSessionResetSet = (): string[] => {
  try {
    const parsed = JSON.parse(sessionStorage.getItem(VERSION_RESET_SESSION_KEY) || '[]');
    return Array.isArray(parsed) ? parsed.filter((v): v is string => typeof v === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * Já rodou um reset PARA esta versão nesta sessão? Usado pelo VersionChecker como
 * cinto de segurança once-per-session-per-version contra loop de modal com HTML stale.
 */
export const hasResetVersionThisSession = (version: string): boolean => readSessionResetSet().includes(version);

// Executa uma etapa de limpeza/restauração isolada: uma falha é logada e engolida
// para não impedir as etapas seguintes nem o reload final.
const run = (label: string, fn: () => void) => {
  try {
    fn();
  } catch (e) {
    Sentry?.logger?.info?.(`[localDataReset] falha em ${label}`, { error: String(e) });
  }
};

// Cookies preservados: auth + filtros globais do header (contexto de navegação).
const PRESERVED_COOKIES = [...PRESERVE_COOKIES, ...PRESERVE_FILTER_COOKIES];

// Apaga todos os cookies exceto os preservados. Cobre path=/ com e sem domain,
// porque os cookies de filtro são gravados com domain=cookieDomain.
const clearNonPreservedCookies = () => {
  document.cookie.split(';').forEach(cookie => {
    const name = cookie.split('=')[0].trim();
    if (!name || PRESERVED_COOKIES.includes(name)) return;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;domain=${URLS.cookieDomain}`;
  });
};

// Apaga um IndexedDB de cache sem nunca bloquear o reload: se a conexão estiver
// aberta (onblocked) ou demorar, resolve por timeout e segue o fluxo.
const deleteIdb = (name: string) =>
  new Promise<void>(resolve => {
    if (PROTECTED_IDB.includes(name)) return resolve();

    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      resolve();
    };
    const timer = setTimeout(done, 1500);

    try {
      const req = indexedDB.deleteDatabase(name);
      req.onsuccess = done;
      req.onerror = done;
      req.onblocked = done;
    } catch {
      done();
    }
  });

/**
 * Reset de dados locais na virada de versão, PRESERVANDO a sessão.
 *
 * Fluxo: snapshot do que preservar → limpezas isoladas → restaura auth →
 * marca a versão nova → reload. Nunca toca em `upload-db` (outbox).
 *
 * @param newVersion versão detectada no <meta app-version>; gravada como marcador anti-loop.
 */
export const resetLocalData = async (newVersion?: string | null): Promise<void> => {
  // 1. Snapshot em memória do que deve sobreviver.
  const lsSnapshot: Record<string, string | null> = {};
  [...PRESERVE_LS, ...CONTROL_LS].forEach(key => {
    lsSnapshot[key] = localStorage.getItem(key);
  });

  const cookieSnapshot: Record<string, string | undefined> = {};
  PRESERVE_COOKIES.forEach(name => {
    cookieSnapshot[name] = getCookie(name);
  });

  // O set do cinto vive em sessionStorage, que o passo 2 abaixo limpa por inteiro.
  // Snapshot aqui para preservá-lo (senão perderíamos o registro de resets anteriores
  // e o cinto oscilaria em loop no cenário de HTML stale).
  const sessionResetSnapshot = readSessionResetSet();

  // 2. Limpezas isoladas (cada uma em try/catch; nenhuma impede o reload).
  run('localStorage.clear', () => localStorage.clear());
  run('sessionStorage.clear', () => sessionStorage.clear());
  run('cookies', clearNonPreservedCookies);
  await deleteIdb('qrcode-db'); // upload-db/outbox fica de fora (PROTECTED_IDB).

  // 3. Restaura a sessão: localStorage (auth + permissões) e cookies de auth.
  run('restore-localStorage', () => {
    Object.entries(lsSnapshot).forEach(([key, value]) => {
      if (value !== null) localStorage.setItem(key, value);
    });
  });
  run('restore-cookies', () => {
    const { authToken, refreshToken } = cookieSnapshot;
    // authToken/refreshToken também sobreviveram ao passo 2 (não estão na lista de
    // apagar); reescrevê-los do snapshot é reforço. signIn:session idem, por preservação.
    if (!authToken || !refreshToken) return;
    // expiresIn vem do lkp_auth_ctx preservado (o cookie não expõe expiry via document.cookie);
    // passar aqui evita reescrever o authToken como cookie de sessão (sem expiry).
    let expiresIn: string | undefined;
    try {
      expiresIn = JSON.parse(lsSnapshot[localStorageKeys.authContext] || '{}')?.expiresIn ?? undefined;
    } catch {
      expiresIn = undefined;
    }
    saveTokensToCookies(authToken, refreshToken, expiresIn);
  });

  // Nunca gravar undefined/null em app-version: corromperia o marcador anti-loop
  // e reabriria a modal pra sempre. Fallback: relê a <meta> do DOM; se nem isso
  // existir, mantém o valor antigo (restaurado no passo 3) — melhor que lixo.
  const metaVersion = document.querySelector('meta[name="app-version"]')?.getAttribute('content') || undefined;
  const resolvedVersion = newVersion || metaVersion;

  // 4. Marca a versão nova (vence o snapshot de CONTROL_LS) e a flag anti-loop.
  run('version-markers', () => {
    if (resolvedVersion) localStorage.setItem(APP_VERSION_KEY, resolvedVersion);
    localStorage.setItem(FORCE_RESET_KEY, 'true');
  });

  // 4b. Registra a versão resetada no set de sessão (cinto once-per-session-per-version).
  // Se o HTML vier de cache stale após o reload, o VersionChecker vê esta marca e não
  // reabre a modal — "atualiza uma vez e para". Só reseta ao fechar a aba.
  run('session-reset-marker', () => {
    if (!resolvedVersion) return;
    // Parte do snapshot pré-limpeza para acumular (não do valor atual, já zerado).
    const acted = [...sessionResetSnapshot];
    if (!acted.includes(resolvedVersion)) acted.push(resolvedVersion);
    sessionStorage.setItem(VERSION_RESET_SESSION_KEY, JSON.stringify(acted));
  });

  // 5. Recarrega para aplicar a versão nova.
  window.location.reload();
};

export default resetLocalData;
